import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Download, Loader2, RefreshCw, Star, Sparkles, BarChart3,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, value, sub, accent = false }) => (
  <div className={`rounded-2xl border p-5 ${
    accent
      ? "bg-[#00FF88]/[0.06] border-[#00FF88]/30"
      : "bg-[#0A0A0A] border-white/8"
  }`}>
    <div className="font-mono-c text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2">{label}</div>
    <div className="font-mono-c font-bold text-3xl text-white">{value}</div>
    {sub && <div className="font-mono-c text-[11px] text-white/40 mt-1">{sub}</div>}
  </div>
);

const Bar = ({ label, value, max, total }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  const sharePct = total > 0 ? ((value / total) * 100).toFixed(1) : "0";
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="text-white/80 font-medium">{label}</span>
        <span className="font-mono-c text-white/60 text-xs">
          {value} <span className="text-white/30">·</span> {sharePct}%
        </span>
      </div>
      <div className="h-2 rounded-full bg-white/[0.04] overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-[#00FF88] to-[#14F195] transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const Section = ({ title, eyebrow, children }) => (
  <section className="rounded-3xl border border-white/8 bg-[#0A0A0A] p-6 lg:p-7">
    <div className="font-mono-c text-[10px] uppercase tracking-[0.22em] text-[#00FF88] mb-2">// {eyebrow}</div>
    <h3 className="font-display font-extrabold text-xl text-white mb-5">{title}</h3>
    {children}
  </section>
);

const Breakdown = ({ items, total, empty }) => {
  if (!items || items.length === 0) {
    return <div className="text-white/40 text-sm">{empty}</div>;
  }
  const max = items[0][1];
  return (
    <div>
      {items.map(([label, count]) => (
        <Bar key={label} label={label} value={count} max={max} total={total} />
      ))}
    </div>
  );
};

const PostRepaymentAnalytics = ({ token }) => {
  const [data, setData] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const [a, l] = await Promise.all([
        axios.get(`${API}/pilot/post-repayment/analytics`, { headers: { "X-Admin-Token": token } }),
        axios.get(`${API}/pilot/post-repayment/list`, { headers: { "X-Admin-Token": token } }),
      ]);
      setData(a.data);
      setResponses(l.data.items || []);
    } catch {
      toast.error("Failed to load post-repayment analytics");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  const download = async (kind) => {
    try {
      const res = await axios.get(`${API}/pilot/post-repayment/export.${kind}`, {
        headers: { "X-Admin-Token": token }, responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `post_repayment_surveys.${kind}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(`Failed to download ${kind.toUpperCase()}`);
    }
  };

  if (loading) {
    return (
      <div className="py-20 text-center text-white/40" data-testid="pr-analytics-loading">
        <Loader2 className="w-6 h-6 animate-spin inline" />
      </div>
    );
  }
  if (!data) return null;

  const total = data.total_surveys_completed || 0;

  return (
    <div data-testid="pr-analytics-tab" className="space-y-6">
      {/* header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88] mb-1">// Post-Repayment Analytics</div>
          <h2 className="font-display font-black text-2xl text-white tracking-tight">Customer satisfaction · product validation</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setRefreshing(true); load(); }}
            className="inline-flex items-center gap-2 text-xs font-bold px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-white/30 text-white/80"
            data-testid="pr-analytics-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={() => download("csv")}
            className="inline-flex items-center gap-2 text-xs font-bold px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-white/30 text-white/80"
            data-testid="pr-analytics-csv"
          >
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
          <button
            onClick={() => download("xlsx")}
            className="inline-flex items-center gap-2 text-xs font-bold px-4 py-2 rounded-full bg-[#00FF88] text-black hover:opacity-90"
            data-testid="pr-analytics-xlsx"
          >
            <Download className="w-3.5 h-3.5" /> Excel
          </button>
        </div>
      </div>

      {/* Topline stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pr-topline">
        <Stat
          label="Surveys completed"
          value={total}
          sub={`${data.total_repaid_applications} repaid apps total`}
          accent
        />
        <Stat
          label="Completion rate"
          value={`${data.survey_completion_rate_pct}%`}
          sub="of repaid users"
        />
        <Stat
          label="Avg helpfulness"
          value={data.avg_helpfulness_rating ?? "—"}
          sub="1–5 scale"
        />
        <Stat
          label="NPS score"
          value={data.nps_score ?? "—"}
          sub={`avg ${data.avg_nps ?? "—"} / 10`}
        />
      </div>

      {/* Yes-percent intent metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="pr-intent">
        <Stat label="Would use again (Yes)" value={`${data.would_use_again_yes_pct}%`} accent />
        <Stat label="Testimonial permission (Yes)" value={`${data.testimonial_permission_yes_pct}%`} />
        <Stat label="Early access interest (Yes)" value={`${data.future_beta_interest_yes_pct}%`} />
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Section title="What Floats covered" eyebrow="Use cases">
          <Breakdown items={data.use_case_breakdown} total={total} empty="No responses yet." />
        </Section>
        <Section title="Without GigAfloat, alternatives" eyebrow="Counterfactual">
          <Breakdown items={data.alternative_option_breakdown} total={total} empty="No responses yet." />
        </Section>
        <Section title="Most-requested future Float amounts" eyebrow="Demand">
          <Breakdown items={data.future_float_amount_breakdown} total={total} empty="No responses yet." />
        </Section>
        <Section title="Recent submissions" eyebrow="Live feed">
          {data.recent_responses && data.recent_responses.length > 0 ? (
            <ul className="space-y-3">
              {data.recent_responses.map((r, i) => (
                <li key={i} className="rounded-2xl bg-[#050505] border border-white/8 p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white truncate">
                      {r.first_name || "Anonymous"}
                    </div>
                    <div className="font-mono-c text-[11px] text-white/40">
                      {new Date(r.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 text-xs">
                    <span className="inline-flex items-center gap-1 text-[#00FF88]">
                      <Star className="w-3.5 h-3.5 fill-current" /> {r.helpfulness_rating}/5
                    </span>
                    <span className="font-mono-c text-white/60">NPS {r.nps_score}</span>
                    <span className="font-mono-c text-white/60 uppercase text-[10px]">{r.would_use_again}</span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-white/40 text-sm">No responses yet.</div>
          )}
        </Section>
      </div>

      {/* Full table */}
      <Section title="All survey responses" eyebrow={`${responses.length} total`}>
        <div className="overflow-x-auto rounded-2xl border border-white/8">
          <table className="min-w-full text-sm" data-testid="pr-full-table">
            <thead className="bg-white/[0.03] text-white/55">
              <tr>
                <th className="text-left px-3 py-2 font-mono-c text-[10px] uppercase tracking-widest">When</th>
                <th className="text-left px-3 py-2 font-mono-c text-[10px] uppercase tracking-widest">Name</th>
                <th className="text-left px-3 py-2 font-mono-c text-[10px] uppercase tracking-widest">Float</th>
                <th className="text-left px-3 py-2 font-mono-c text-[10px] uppercase tracking-widest">⭐</th>
                <th className="text-left px-3 py-2 font-mono-c text-[10px] uppercase tracking-widest">NPS</th>
                <th className="text-left px-3 py-2 font-mono-c text-[10px] uppercase tracking-widest">Again</th>
                <th className="text-left px-3 py-2 font-mono-c text-[10px] uppercase tracking-widest">Use cases</th>
                <th className="text-left px-3 py-2 font-mono-c text-[10px] uppercase tracking-widest">Testimonial</th>
              </tr>
            </thead>
            <tbody className="text-white/80">
              {responses.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-white/40">No responses yet.</td></tr>
              )}
              {responses.map((r) => (
                <tr key={r.id} className="border-t border-white/5">
                  <td className="px-3 py-2 font-mono-c text-[11px] text-white/55">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-3 py-2">{r.first_name || "—"}</td>
                  <td className="px-3 py-2 font-mono-c">${r.float_amount ?? "—"}</td>
                  <td className="px-3 py-2 text-[#00FF88] font-mono-c">{r.helpfulness_rating}/5</td>
                  <td className="px-3 py-2 font-mono-c">{r.nps_score}</td>
                  <td className="px-3 py-2 text-xs uppercase">{r.would_use_again}</td>
                  <td className="px-3 py-2 text-xs text-white/70 max-w-[280px] truncate">{(r.float_use_case || []).join(", ")}</td>
                  <td className="px-3 py-2 text-xs uppercase">{r.testimonial_permission}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
};

export default PostRepaymentAnalytics;
